import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Alert,
  Chip,
  Stack,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Verified as VerifyIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { licenseApi } from 'utils/api';

const LICENSE_STATUS_DESCRIPTIONS = {
  VALID: '정상',
  EXPIRING_SOON: '만료 임박',
  GRACE_PERIOD: '유예 기간',
  EXPIRED: '만료됨',
  TAMPERED: '변조됨',
  NOT_YET_VALID: '시작일 전',
  INVALID: '유효하지 않음',
};

export default function LicenseVerifyPage() {
  const [licenseContent, setLicenseContent] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleVerify = async () => {
    if (!licenseContent.trim()) {
      toast.error('라이선스 내용을 입력해주세요.');
      return;
    }

    setLoading(true);
    try {
      const response = await licenseApi.verify(licenseContent);
      const verificationResult = response.data;
      setResult(verificationResult);

      if (verificationResult.status === 'VALID') {
        toast.success('라이선스가 유효합니다.');
      } else if (verificationResult.status === 'EXPIRING_SOON') {
        toast.success('라이선스가 유효합니다. (만료 임박)');
      } else {
        toast.error(`검증 실패: ${verificationResult.message}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || '검증에 실패했습니다.');
      setResult({
        status: 'INVALID',
        message: error.response?.data?.error || error.message,
        license: null,
        warnings: [],
        details: {},
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'VALID': return 'success';
      case 'EXPIRING_SOON':
      case 'GRACE_PERIOD': return 'warning';
      case 'EXPIRED':
      case 'TAMPERED':
      case 'INVALID': return 'error';
      default: return 'info';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'VALID': return <CheckIcon color="success" />;
      case 'EXPIRING_SOON':
      case 'GRACE_PERIOD': return <WarningIcon color="warning" />;
      default: return <ErrorIcon color="error" />;
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        라이선스 검증
      </Typography>

      <TextField
        fullWidth
        multiline
        rows={6}
        label="라이선스 내용"
        placeholder="-----BEGIN INTOSOFT LICENSE-----"
        value={licenseContent}
        onChange={(e) => setLicenseContent(e.target.value)}
        sx={{ mb: 2 }}
      />
      <Button
        variant="contained"
        startIcon={<VerifyIcon />}
        onClick={handleVerify}
        disabled={loading}
        size="large"
      >
        {loading ? '검증 중...' : '라이선스 검증'}
      </Button>

      {/* 검증 결과 */}
      {result && (
        <Box sx={{ mt: 4 }}>
          <Divider sx={{ mb: 3 }} />

          <Alert
            severity={getStatusColor(result.status)}
            icon={getStatusIcon(result.status)}
            sx={{ mb: 3 }}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              {LICENSE_STATUS_DESCRIPTIONS[result.status] || result.status}
            </Typography>
            <Typography variant="body2">{result.message}</Typography>
          </Alert>

          {result.warnings?.length > 0 && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight="bold">경고</Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {result.warnings.map((warning, i) => (
                  <li key={i}>{warning}</li>
                ))}
              </ul>
            </Alert>
          )}

          {result.license && (
            <Grid container spacing={3}>
              {/* 기본 정보 */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      기본 정보
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                        <ListItemText
                          primary="라이선스 ID"
                          secondary={result.license.license.id}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                        <ListItemText
                          primary="고객사"
                          secondary={result.license.license.customer.name}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                        <ListItemText
                          primary="사업명"
                          secondary={result.license.license.project}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                        <ListItemText
                          primary="제품명"
                          secondary={result.license.license.product.name}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                        <ListItemText
                          primary="구분"
                          secondary={result.license.license.type.tier === 'DEV' ? '개발' : '정식'}
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* 유효 기간 */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      유효 기간
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                        <ListItemText
                          primary="시작일"
                          secondary={result.license.license.validity.validFrom || '-'}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                        <ListItemText
                          primary="만료일"
                          secondary={
                            result.license.license.validity.isPerpetual
                              ? '영구 라이선스'
                              : result.license.license.validity.validUntil || '-'
                          }
                        />
                      </ListItem>
                      {result.details.daysUntilExpiration != null && (
                        <ListItem>
                          <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                          <ListItemText
                            primary="남은 기간"
                            secondary={
                              result.details.daysUntilExpiration >= 0
                                ? `${result.details.daysUntilExpiration}일`
                                : `${Math.abs(result.details.daysUntilExpiration)}일 초과`
                            }
                          />
                        </ListItem>
                      )}
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* 제약 조건 */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      제약 조건
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                        <ListItemText
                          primary="최대 코어"
                          secondary={
                            result.license.license.constraints.cores.max === 0
                              ? '무제한'
                              : result.license.license.constraints.cores.max
                          }
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                        <ListItemText
                          primary="도메인 정책"
                          secondary={result.license.license.constraints.domain.policy}
                        />
                      </ListItem>
                      {result.license.license.constraints.domain.allowed?.length > 0 && (
                        <ListItem>
                          <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                          <ListItemText
                            primary="허용 도메인"
                            secondary={
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                                {result.license.license.constraints.domain.allowed.map((d) => (
                                  <Chip key={d} label={d} size="small" variant="outlined" />
                                ))}
                              </Stack>
                            }
                          />
                        </ListItem>
                      )}
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* 바인딩 */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      하드웨어 바인딩
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                        <ListItemText
                          primary="바인딩 모드"
                          secondary={result.license.license.binding.mode}
                        />
                      </ListItem>
                      {result.license.license.binding.allowedMacAddresses?.length > 0 && (
                        <ListItem>
                          <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                          <ListItemText
                            primary="MAC 주소"
                            secondary={
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                                {result.license.license.binding.allowedMacAddresses.map((m) => (
                                  <Chip key={m} label={m} size="small" variant="outlined" />
                                ))}
                              </Stack>
                            }
                          />
                        </ListItem>
                      )}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </Box>
      )}
    </Box>
  );
}
